import { useCallback, useMemo } from "react";

import { useMsal } from "@azure/msal-react";
import { Authentication, Dashboard } from "components";
import { AppLanguages, QueryClientGlobal, UseLoginLocal } from "configs";
import { alert, confirm } from "contexts";
import { useAuth, usePermission } from "hooks";
import { NotFoundPage, ForbiddenPage } from "pages";
import { useTranslation } from "react-i18next";
import { Route, Routes } from "react-router-dom";
import { authLogoutService } from "services";
import { useAppDispatch, useAppSelector } from "stores";
import { authSlice } from "stores/Auth";
import { systemSlice } from "stores/System";
import { ThemeUtils } from "utils";

import {
  AuthenticationRoutes,
  AuthenticationSuspenseWrapper,
} from "./authentication";
import { DashboardRoutes, DashboardSuspenseWrapper } from "./dashboard";
import { RequireAuth, ProtectedAuth } from "./private";
import { getSidebarBottomMenuRoutes, getSidebarMenuRoutes } from "./sidebar";

const AppRoutes = () => {
  const dispatch = useAppDispatch();

  const { t } = useTranslation();

  const { language } = useAppSelector((state) => state.system);

  const { canAccess } = usePermission();

  const { isInitializing, isLoggedIn, data } = useAuth();
  const { instance } = useMsal();

  const sidebarMenuBasedOnPermissions = useMemo(() => {
    return getSidebarMenuRoutes(t, canAccess);
  }, [canAccess, t]);

  const sidebarBottomMenuBasedOnPermissions = useMemo(() => {
    return getSidebarBottomMenuRoutes(t, canAccess);
  }, [t, canAccess]);

  const handleLogout = useCallback(() => {
    confirm({
      title: t("logout"),
      content: t("doYouWantToLogout"),
      okLabel: t("logout"),
    })
      .then(() => {
        authLogoutService()
          .then(() => {
            QueryClientGlobal?.clear();
            dispatch(authSlice.actions.logout());
            dispatch(systemSlice.actions.reset());
            ThemeUtils.clearTheme();

            if (UseLoginLocal) {
              const allAccounts = instance.getAllAccounts();

              if (allAccounts?.length) {
                instance?.logoutPopup();
              }
            }
          })
          .catch(() => {
            alert({
              title: t("logoutFailed"),
              content: t("pleaseTryAgain"),
            })
              .then(() => {})
              .catch(() => {});
          });
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLanguageChange = useCallback((value: string) => {
    dispatch(systemSlice.actions.changeLanguage(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isInitializing) return null;

  return (
    <Routes>
      <Route
        element={
          <RequireAuth>
            <Dashboard
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              menu={sidebarMenuBasedOnPermissions as any}
              bottomMenu={sidebarBottomMenuBasedOnPermissions}
              userInfo={
                isLoggedIn && data
                  ? { email: data.email, name: data.fullName }
                  : undefined
              }
              languages={AppLanguages}
              defaultLanguage={language}
              onLogout={handleLogout}
              onLanguageChange={handleLanguageChange}
            />
          </RequireAuth>
        }
      >
        {DashboardRoutes.map(({ useSuspense, element, ...innerProps }) => {
          return (
            <Route
              key={innerProps.path}
              {...innerProps}
              element={
                useSuspense ? (
                  <DashboardSuspenseWrapper key={innerProps.path}>
                    {element}
                  </DashboardSuspenseWrapper>
                ) : (
                  element
                )
              }
            />
          );
        })}
      </Route>
      {UseLoginLocal && (
        <Route
          element={
            <ProtectedAuth>
              <Authentication />
            </ProtectedAuth>
          }
        >
          {AuthenticationRoutes.map(
            ({ useSuspense, element, ...innerProps }) => (
              <Route
                key={innerProps.path}
                {...innerProps}
                element={
                  useSuspense ? (
                    <AuthenticationSuspenseWrapper key={innerProps.path}>
                      {element}
                    </AuthenticationSuspenseWrapper>
                  ) : (
                    element
                  )
                }
              />
            )
          )}
        </Route>
      )}
      <Route path="/forbidden" element={<ForbiddenPage />} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
};

export default AppRoutes;
